// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import React, { useEffect, useCallback, useMemo, memo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../../../store';
import { useMsal } from '../../../../recruiter/useMsal';
import Sidebar from '../../../../components/RecruiterSidebar';
import styles from './jobsRoles.module.scss';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import PageLoader from '../../../../components/PageLoader';
import { fetchInterviewHostDashboard, Job } from '../../../../store/interviewHostSlice';
import NewJobModal from '../NewJobModal';
import DeleteJobsModal from './DeleteJobsModal';
import { formatNumber, getDeltaSign } from '../../../../utils/numberUtils';

// Type Definitions
interface MetricCard {
  value: string;
  label: string;
  subtext: string;
}

type BadgeColor = 'warning' | 'success' | 'info' | 'error';

interface StatusBadge {
  text: string;
  color: BadgeColor;
}

// Utility Functions - Moved outside component for better performance
const parsePipelineLabel = (label: string): StatusBadge => {
  const lowerLabel = label.toLowerCase();

  if (lowerLabel.includes('to review') || lowerLabel.includes('report ready')) {
    return { text: label, color: 'warning' };
  }
  if (lowerLabel.includes('approved') || lowerLabel.includes('completed')) {
    return { text: label, color: 'success' };
  }
  if (lowerLabel.includes('invited')) {
    return { text: label, color: 'info' };
  }
  if (lowerLabel.includes('declined') || lowerLabel.includes('decided')) {
    return { text: label, color: 'error' };
  }

  return { text: label, color: 'info' };
};

const getStatusClassName = (status: string): string => {
  return status.toLowerCase() === 'active' ? styles.statusOpen : styles.statusOnHold;
};

const getBadgeClassName = (color: BadgeColor): string => {
  const badgeMap: Record<BadgeColor, string> = {
    warning: styles.badgeWarning,
    success: styles.badgeSuccess,
    info: styles.badgeInfo,
    error: styles.badgeError
  };
  return badgeMap[color] || styles.badgeWarning;
};

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

const numberOrZero = (value: unknown): number => (isFiniteNumber(value) ? value : 0);

// Memoized Sub-Components
const MetricCardComponent = memo(({ metric }: { metric: MetricCard }) => (
  <div className={styles.metricCard}>
    <div className={styles.metricValue}>{metric.value}</div>
    <div className={styles.metricLabel}>{metric.label}</div>
    <div className={styles.metricSubtext}>{metric.subtext}</div>
  </div>
));
MetricCardComponent.displayName = 'MetricCard';

interface JobCardProps {
  job: Job;
  onJobClick: (jobId: string) => void;
  isSelected: boolean;
  onSelect: (jobId: string, event: React.MouseEvent) => void;
}

const JobCardComponent = memo(({ job, onJobClick, isSelected, onSelect }: JobCardProps) => {
  const statusBadge = useMemo(() => parsePipelineLabel(job.pipeline_label), [job.pipeline_label]);

  const handleClick = useCallback(() => {
    onJobClick(job.job_id);
  }, [job.job_id, onJobClick]);

  const handleCheckboxClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onSelect(job.job_id, e);
    },
    [job.job_id, onSelect]
  );

  return (
    <div className={`${styles.jobCard} ${isSelected ? styles.jobCardSelected : ''}`} onClick={handleClick}>
      <div className={styles.jobCardCheckbox} onClick={handleCheckboxClick}>
        {isSelected ? (
          <CheckBoxIcon className={styles.checkboxIconChecked} />
        ) : (
          <CheckBoxOutlineBlankIcon className={styles.checkboxIconUnchecked} />
        )}
      </div>

      <div className={styles.jobHeader}>
        <div className={styles.jobHeaderLeft}>
          <span className={`${styles.status} ${getStatusClassName(job.status)}`}>{job.status}</span>
          <span className={styles.postedTime}>Posted {job.posted_days_ago}d ago</span>
        </div>
      </div>

      <h3 className={styles.jobTitle}>{job.title}</h3>

      <p className={styles.jobMeta}>
        {job.department} · {job.location}
        {job.business_unit && ` · ${job.business_unit}`}
      </p>

      <div className={styles.jobFooter}>
        <div className={styles.jobFooterLeft}>
          <PeopleOutlineIcon sx={{ fontSize: 16 }} />
          <span>{job.candidate_count} candidates</span>
        </div>
        <div className={`${styles.badge} ${getBadgeClassName(statusBadge.color)}`}>{statusBadge.text}</div>
      </div>

      <div className={styles.progressBar}>
        <div className={styles.progressFill} style={{ width: `${job.progress_percent}%` }} />
      </div>
    </div>
  );
});
JobCardComponent.displayName = 'JobCard';

// Main Component
const JobsRoles: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { instance } = useMsal();
  const user = useSelector((state: RootState) => state.auth.user);
  const { data: dashboardData, loading, error } = useSelector((state: RootState) => state.interviewHost);
  const [showNewJobModal, setShowNewJobModal] = useState(false);
  const [selectedJobIds, setSelectedJobIds] = useState<Set<string>>(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Fetch fresh data on every visit to the page
  useEffect(() => {
    dispatch(fetchInterviewHostDashboard());
  }, [dispatch]);

  // Event handlers - Memoized to prevent unnecessary re-renders
  const handleLogout = useCallback(async () => {
    try {
      // logoutPopup already performs the full-page redirect to /api/sso/logout;
      // navigating again here would cancel that request before the server can
      // destroy the session and clear the cookie.
      await instance.logoutPopup({
        postLogoutRedirectUri: '/login'
      });
    } catch (err) {
      console.error('Logout failed:', err);
    }
  }, [instance]);

  const handleBackToHome = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const handleRefresh = useCallback(() => {
    dispatch(fetchInterviewHostDashboard());
  }, [dispatch]);

  // Filter out usage restricted jobs - Memoized for performance
  const visibleJobs = useMemo(() => {
    if (!dashboardData) return [];
    return dashboardData.jobs.filter((job) => !job.usage_restricted);
  }, [dashboardData]);

  const handleJobClick = useCallback(
    (jobId: string) => {
      navigate(`/jobs/${jobId}/candidates`);
    },
    [navigate]
  );

  const handleNewJobSuccess = useCallback(() => {
    dispatch(fetchInterviewHostDashboard());
  }, [dispatch]);

  const handleJobSelect = useCallback((jobId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedJobIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(jobId)) {
        newSet.delete(jobId);
      } else {
        newSet.add(jobId);
      }
      return newSet;
    });
  }, []);

  const handleDeleteClick = useCallback(() => {
    if (selectedJobIds.size > 0) {
      setShowDeleteModal(true);
    }
  }, [selectedJobIds.size]);

  const handleDeleteSuccess = useCallback(() => {
    setSelectedJobIds(new Set());
    setShowDeleteModal(false);
  }, []);

  // Computed metric cards - Memoized to prevent recalculation
  const metricCards = useMemo<MetricCard[]>(() => {
    if (!dashboardData) return [];

    const { metrics } = dashboardData;
    const avgInviteDays = metrics.average_time_to_invite_days ?? metrics.average_interview_host_minutes;
    const avgInviteDelta = metrics.average_time_to_invite_delta_days ?? metrics.average_interview_host_delta_minutes;
    const deltaSign = getDeltaSign(metrics.open_roles_delta_month);
    const avgTimeDeltaSign = getDeltaSign(avgInviteDelta);
    const averageInviteTime = isFiniteNumber(avgInviteDays) ? `${formatNumber(avgInviteDays, 1)}d` : 'N/A';
    const averageInviteDelta = isFiniteNumber(avgInviteDelta)
      ? `${avgTimeDeltaSign} ${formatNumber(Math.abs(avgInviteDelta), 1)}d vs last month`
      : isFiniteNumber(avgInviteDays)
        ? 'Invites this month'
        : 'No invite data';

    return [
      {
        value: dashboardData.jobs.length.toString(),
        label: 'Total roles',
        subtext: `${deltaSign} ${numberOrZero(metrics.open_roles_delta_month)} new openings this month`
      },
      {
        value: numberOrZero(metrics.total_candidates).toString(),
        label: 'Total candidates',
        subtext: 'across all roles'
      },
      {
        value: numberOrZero(metrics.interviews_completed_this_week).toString(),
        label: 'Completed this week',
        subtext: `${numberOrZero(metrics.interviews_completed)} total completed candidates`
      },
      {
        value: averageInviteTime,
        label: 'Avg time to invite',
        subtext: averageInviteDelta
      }
    ];
  }, [dashboardData]);

  // Loading state
  if (loading) {
    // Sidebar removed - only show on /app/v1 and /app/v2 routes
    return <PageLoader label="Loading jobs..." activeNav="home" showSidebar={false} />;
  }

  // Error state
  if (error || !dashboardData) {
    return (
      <div className={styles.container}>
        {/* Sidebar removed - only show on /app/v1 and /app/v2 routes */}
        {/* <Sidebar activeNav="home" user={user || undefined} onLogout={handleLogout} /> */}
        <main className={styles.mainContent}>
          <div className={styles.errorContainer}>
            <h2>Failed to load dashboard</h2>
            <p>{error || 'An unexpected error occurred.'}</p>
            <button className={styles.btnRefresh} onClick={handleRefresh}>
              <RefreshIcon sx={{ fontSize: 18 }} />
              Try Again
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Sidebar removed - only show on /app/v1 and /app/v2 routes */}
      {/* <Sidebar activeNav="home" user={user || undefined} onLogout={handleLogout} /> */}
      <main className={styles.mainContent}>
        <div className={styles.breadcrumb}>
          <button className={styles.breadcrumbLink} onClick={handleBackToHome}>
            Home
          </button>
          <ChevronRightIcon className={styles.breadcrumbSeparator} />
          <span className={styles.breadcrumbCurrent}>Jobs</span>
        </div>

        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h1 className={styles.title}>Job Roles</h1>
            <p className={styles.subtitle}>
              {selectedJobIds.size > 0
                ? `${selectedJobIds.size} ${selectedJobIds.size === 1 ? 'job' : 'jobs'} selected`
                : 'All open requisitions — click a job to see its candidates'}
            </p>
          </div>
          <div className={styles.headerRight}>
            {selectedJobIds.size > 0 && (
              <button className={styles.btnDelete} onClick={handleDeleteClick}>
                <DeleteIcon sx={{ fontSize: 18 }} />
                Delete ({selectedJobIds.size})
              </button>
            )}
            <button className={styles.btnRefresh} onClick={handleRefresh} disabled={loading}>
              <RefreshIcon sx={{ fontSize: 18 }} />
              Refresh
            </button>
            <button className={styles.btnNewJob} onClick={() => setShowNewJobModal(true)}>
              <AddIcon sx={{ fontSize: 18 }} />
              New job
            </button>
          </div>
        </div>

        <div className={styles.metricsGrid}>
          {metricCards.map((metric, index) => (
            <MetricCardComponent key={`metric-${index}`} metric={metric} />
          ))}
        </div>

        <div className={styles.jobsGrid}>
          {visibleJobs.map((job) => (
            <JobCardComponent
              key={job.job_id}
              job={job}
              onJobClick={handleJobClick}
              isSelected={selectedJobIds.has(job.job_id)}
              onSelect={handleJobSelect}
            />
          ))}
        </div>
      </main>

      <NewJobModal isOpen={showNewJobModal} onClose={() => setShowNewJobModal(false)} onSuccess={handleNewJobSuccess} />

      <DeleteJobsModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        jobIds={Array.from(selectedJobIds)}
        jobTitles={Object.fromEntries(visibleJobs.map((job) => [job.job_id, job.title]))}
        onSuccess={handleDeleteSuccess}
      />
    </div>
  );
};

export default memo(JobsRoles);
